import { Globalization } from '../data/globalization';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { EnvironmentModule } from '../manifest/environment-modules';
import { RpcNotification } from '../rpc/notification/rpc-notification-model';
import { RpcWorkItem } from '../rpc/work-item/rpc-work-item-model';
import { NotificationLinkType } from './notification-link-type';
import { IFrameService } from './notification-manager';
import { NotificationMessage } from './notification-message';
import { NotificationState } from './notification-state';
import { PowerShellNotification, PowerShellWorkItemMessage } from './powershell-notification';
import { SocketMessage } from './socket-signalr';
import { RecoveredWorkItem } from './work-item-request';

/**
 * Notification changed event type.
 */
export enum NotificationChangeEvent {
    Initialized,
    InitializationFailed,
    Add,
    Remove,
    Change
}

/**
 * Notification changed event packet.
 */
export interface NotificationEvent {
    changeEvent: NotificationChangeEvent;
    notification?: Notification;
}

/**
 * Internal notification object converted from ClientNotification object.
 * Contains data to track notifications.
 */
export class Notification {
    private strings: Strings = MsftSme.getStrings<Strings>();

    /**
     * Work item passed from RPC or client on shell.
     */
    private workItem: RpcWorkItem;

    /**
     * The type ID of work item.
     */
    public typeId: string;

    /**
     * The node name.
     */
    public nodeName: string;

    /**
     * The module name.
     */
    public moduleName: string;

    /**
     * The entry point name within the module
     */
    public entryPointName: string;

    /**
     * The module display name.
     */
    public moduleDisplayName: string;

    /**
     * Object included last response.
     */
    public object: any;

    /**
     * The state of notification.
     */
    public state: NotificationState;

    /**
     * The title of work item to display user. (localized)
     */
    public title: string;

    /**
     * @depracated
     * The description of work item to display user. (localized)
     */
    public description: string;

    /**
     * The message. (localized)
     */
    public message: string;

    /**
     * Possible solution message to address error. (localized)
     */
    public solutionMessage: string;

    /**
     * True if the notification was created from recover, updates from messages should be taken
     */
    public isFromRecover = false;

    /**
     * True if the notification is disabled and should not be shown to the user
     */
    public isDisabled = false;

    /**
     * The start timestamp as a formatted globalized string
     */
    public startTimestamp: string;

    /**
     * The last changed timestamp as a formatted globalized string
     */
    public changedTimestamp: string;

    /**
     * The last changed timestamp as a number
     */
    public changedTimestampValue: number;

    /**
     * The end timestamp as a formatted globalized string
     */
    public endTimestamp: string;

    /**
     * The progress percent.
     */
    public progressPercent: number;

    /**
     * The success link to navigate to the object view. (optional)
     * At default, it brings to the home page of the module.
     */
    public link: string;

    /**
     * the notification link type of the link
     */
    public linkType: NotificationLinkType;

    /**
     * True if we should show the link in the notification's alert
     * We will show the link if there is a custom link or it is a long running request
     */
    public showLinkInAlert = false;

    /**
     * Marked it's no longer display to include list of notifications.
     */
    public dismissed = false;

    /**
     * The parent URI window.location.pathname.
     */
    public locationPathname: string;

    /**
     * The parent URI window.location.search
     */
    public locationSearch: string;

    /**
     *
     * Tracks if the notification has been read.
     */
    public isUnread = true;

    /**
     * Create notification from WorkItem.
     *
     * @param id the notification ID.
     * @param workItem the RPC work item.
     * @param state the initial state.
     * @param object the object from query result.
     * @return notification the notification object.
     */
    public static createFromWorkItem(
        id: string, workItem: RpcWorkItem, state: NotificationState, iFrameService: IFrameService, object?: any): Notification {
        const notification = new Notification(id);
        notification.workItem = workItem;

        notification.state = state;
        notification.object = object;
        notification.initializeFromWorkItem(workItem, iFrameService);
        return notification;
    }

    /**
     * Create notification from recovered work item.
     *
     * @param recoveredWorkItem the recovered work item.
     * @return notification the notification object.
     */
    public static createFromRecover(recoveredWorkItem: RecoveredWorkItem, iFrameService: IFrameService): Notification {
        const notification = new Notification(recoveredWorkItem.id);
        notification.workItem = recoveredWorkItem.metadata;
        notification.isFromRecover = true;

        if (recoveredWorkItem.failed) {
            notification.state = NotificationState.Error;
            notification.object = { errorType: 'WorkItemRecover', message: recoveredWorkItem.errorMessage };
        } else {
            notification.state = NotificationState.InProgress;
            notification.object = {};
        }

        notification.initializeFromWorkItem(recoveredWorkItem.metadata, iFrameService);
        return notification;
    }

    /**
     * Create notification from instant request.
     *
     * @param client the RPC notification request.
     * @return notification the notification object.
     */
    public static createFromClient(client: RpcNotification, iFrameService: IFrameService): Notification {
        const notification = new Notification(client.id);
        notification.locationPathname = window.location.pathname;
        notification.locationSearch = window.location.search;
        notification.initializeFromInstant(client, iFrameService);
        return notification;
    }

    /**
     * Initializes a new instance of the Notification class.
     *
     * @param id the notification ID.
     */
    constructor(public id: string) { }

    /**
     * Update a notification by work item from RPC.
     * @param id the notification id.
     * @param workItem the work item.
     * @param state the state of the notification.
     * @param object the object from query result.
     */
    public updateFromWorkItem(id: string, workItem: RpcWorkItem, state: NotificationState, object?: any) {
        let changed = false;

        if (this.updateState(state)) {
            changed = true;
            const now = Date.now();
            this.changedTimestampValue = now;
            this.changedTimestamp = this.endTimestamp = this.getGlobalizedTimestamp(now);
        }

        if (this.object !== object) {
            changed = true;
            this.object = object;
        }

        this.updateMessageAndLinkAndTitle(workItem);
        return !this.isDisabled && changed;
    }

    /**
     * Update the notification by socket message from the gateway.
     *
     * @param item the socket message.
     * @return boolean the changed status.
     */
    public updateFromMessage(item: SocketMessage<PowerShellWorkItemMessage>): boolean {
        let changed = false;
        const now = Date.now();
        if (PowerShellNotification.hasError(item)) {
            Logging.logDebug('Notification', '{0}/Error/{1}'.format(this.id, JSON.stringify(item.message.errors)));
            if (this.updateState(NotificationState.Error)) {
                this.changedTimestampValue = now;
                this.changedTimestamp = this.endTimestamp = this.getGlobalizedTimestamp(now);
            }

            this.object = item.message.errors && MsftSme.first(item.message.errors);
            changed = true;
            this.updateMessageAndLinkAndTitle(this.workItem);
            return !this.isDisabled;
        }

        if (PowerShellNotification.hasException(item)) {
            Logging.logDebug('Notification', '{0}/Exception/{1}'.format(this.id, item.message.exception));
            if (this.updateState(NotificationState.Error)) {
                this.changedTimestampValue = now;
                this.changedTimestamp = this.endTimestamp = this.getGlobalizedTimestamp(now);
            }

            this.object = { message: item.message.exception };
            changed = true;
            this.updateMessageAndLinkAndTitle(this.workItem);
            return !this.isDisabled;
        }

        if (PowerShellNotification.hasProgress(item)) {
            Logging.logDebug('Notification', '{0}/Progress/{1}'.format(this.id, JSON.stringify(item.message.progress)));
            if (this.updateState(NotificationState.InProgress)) {
                this.changedTimestampValue = now;
                this.changedTimestamp = this.getGlobalizedTimestamp(now);
            }

            this.object = item.message.progress && MsftSme.last(item.message.progress);
            this.progressPercent = this.object?.percentComplete ?? this.object?.percent;

            if ((this.progressPercent == null || this.progressPercent < 0)
                && this.workItem.progressMessage && this.workItem.progressMessage.indexOf('{{percent}}') >= 0) {
                return false;
            }

            changed = true;
        }

        if (PowerShellNotification.hasData(item)) {
            Logging.logDebug('Notification', '{0}/Data/{1}'.format(this.id, JSON.stringify(item.message.results)));
            this.object = item.message.results && MsftSme.last(item.message.results);
            changed = true;
        }

        if (PowerShellNotification.hasCompleted(item)) {
            Logging.logDebug('Notification', '{0}/Completed'.format(this.id));
            if (this.updateState(NotificationState.Success)) {
                this.changedTimestampValue = now;
                this.changedTimestamp = this.endTimestamp = this.getGlobalizedTimestamp(now);
            }

            changed = true;
        }

        this.updateMessageAndLinkAndTitle(this.workItem);
        return !this.isDisabled && changed;
    }

    /**
     * Update the notification by socket message from the gateway.
     *
     * @param state the state of notification.
     * @param item the socket message.
     * @return boolean the changed status.
     */
    public updateFromNotificationMessage(state: NotificationState, item: NotificationMessage): boolean {
        const now = Date.now();
        this.title = item.title;
        this.message = item.message;
        this.state = state;
        this.changedTimestampValue = now;
        if (state === NotificationState.InProgress) {
            this.changedTimestamp = this.getGlobalizedTimestamp(now);
        } else {
            this.changedTimestamp = this.endTimestamp = this.getGlobalizedTimestamp(now);
        }

        return true;
    }

    /**
     * Update the notification by instant notification message from the client.
     *
     * @param client the instant notification object.
     * @param boolean the changed status.
     */
    public updateFromClient(client: RpcNotification): boolean {
        let changed = false;
        const title = client.title;
        if (this.title !== title) {
            changed = true;
            this.title = title;
        }

        if (this.updateState(client.state)) {
            const now = Date.now();
            this.changedTimestampValue = now;
            this.changedTimestamp = this.getGlobalizedTimestamp(now);
            changed = true;
            if (this.state !== NotificationState.InProgress) {
                this.endTimestamp = this.changedTimestamp;
            }
        }

        const link = this.formatLink(client.link, client.linkType);
        if (this.link !== link) {
            changed = true;
            this.link = link;
            this.linkType = client.linkType;
        }

        if (this.message !== client.message) {
            changed = true;
            this.message = client.message;
        }

        if (this.solutionMessage !== client.solutionMessage) {
            changed = true;
            this.solutionMessage = client.solutionMessage;
        }

        return changed;
    }

    /**
     * Gets the module display name.
     */
    private getModuleDisplayName(iFrameService: IFrameService, sourceName: string, sourceSubName: string): string {
        const activeIFrame = iFrameService ? iFrameService.getActiveToolIFrameData() : null;
        if (activeIFrame && activeIFrame.entryPoint) {
            const entryPoint = activeIFrame.entryPoint;
            this.entryPointName = entryPoint.name;

            if (entryPoint.parentModule) {
                this.moduleName = entryPoint.parentModule.name;
            }
        } else {
            // fallback see if we can get any info from RPC
            this.moduleName = sourceName;
            if (sourceSubName) {
                this.entryPointName = MsftSme.first(sourceSubName.split('#'));
            }
        }

        const environment = MsftSme.self().Environment;

        // if moduleName === environment.name then the source is Shell. Return null to use generic module name
        if (this.moduleName && this.moduleName !== environment.name) {
            const module: EnvironmentModule = MsftSme.find(
                <EnvironmentModule[]>environment.modules,
                value => value.name === this.moduleName);
            if (module) {
                if (this.entryPointName) {
                    const entryPoint = MsftSme.find(module.entryPoints, ep => ep.name === this.entryPointName);
                    if (entryPoint) {
                        return entryPoint.displayName;
                    }
                }

                return module.displayName;
            }

            return this.moduleName;
        }

        return null;
    }

    /**
     * Update the state.
     *
     * @param state the new state.
     * @return boolean the changed state.
     */
    private updateState(state: NotificationState): boolean {
        if (this.state !== state) {
            this.state = state;
            return true;
        }

        return false;
    }

    private initializeFromWorkItem(item: RpcWorkItem, iFrameService: IFrameService): void {
        this.nodeName = item.nodeName;
        this.moduleDisplayName = this.getModuleDisplayName(iFrameService, item.sourceName, item.sourceSubName);
        this.startTimestamp = this.getGlobalizedTimestamp(item.timestamp);
        this.changedTimestamp = this.getGlobalizedTimestamp(item.timestamp);
        this.changedTimestampValue = item.timestamp;
        this.typeId = item.typeId;
        this.updateMessageAndLinkAndTitle(item);
    }

    private updateMessageAndLinkAndTitle(item: RpcWorkItem): void {
        let template: string;
        this.isDisabled = !this.isFromRecover && item.disableAllNotifications;
        switch (this.state) {
            case NotificationState.Started:
                this.title = item.inProgressTitle;
                template = item.startedMessage;
                this.link = this.formatLink(null);
                break;
            case NotificationState.Error:
                this.title = item.errorTitle;
                this.isDisabled = !this.isFromRecover && item.disableErrorNotification;
                this.endTimestamp = this.getGlobalizedTimestamp(item.timestamp);
                template = item.errorMessage;
                this.link = this.formatLink(item.errorLink, item.errorLinkType);
                this.linkType = item.errorLinkType;
                this.showLinkInAlert = true;
                this.moduleDisplayName = item.errorLinkText || this.moduleDisplayName;
                break;
            case NotificationState.InProgress:
                this.title = item.inProgressTitle;
                template = item.progressMessage;
                this.link = this.formatLink(null);
                break;
            case NotificationState.Success:
                this.title = item.successTitle;
                this.endTimestamp = this.getGlobalizedTimestamp(item.timestamp);
                template = item.successMessage;
                this.link = this.formatLink(item.successLink, item.successLinkType);
                this.linkType = item.successLinkType;
                this.moduleDisplayName = item.successLinkText || this.moduleDisplayName;
                this.showLinkInAlert = true;
                break;
            default:
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.NotificationUnsupportedState.message;
                throw new Error(message);
        }

        this.message = this.formatMessage(template);
    }

    private initializeFromInstant(client: RpcNotification, iFrameService: IFrameService): void {
        this.nodeName = client.nodeName;
        this.moduleDisplayName = client.linkText ?
            client.linkText
            : this.getModuleDisplayName(iFrameService, client.sourceName, client.sourceSubName);
        this.startTimestamp = this.getGlobalizedTimestamp(client.timestamp);
        this.changedTimestamp = this.getGlobalizedTimestamp(client.timestamp);
        this.changedTimestampValue = client.timestamp;
        this.title = client.title;
        this.state = client.state;
        this.link = this.formatLink(client.link, client.linkType);
        this.linkType = client.linkType;
        this.message = client.message;
        this.solutionMessage = client.solutionMessage;
        if (this.state !== NotificationState.Started && this.state !== NotificationState.InProgress) {
            this.endTimestamp = this.getGlobalizedTimestamp(client.timestamp);
        }
    }

    private getGlobalizedTimestamp(timestamp: number): string {
        return Globalization.timeOnly(new Date(timestamp));
    }

    private formatLink(link: string, linkType?: NotificationLinkType): string {
        // href example: "http://localhost:4400/apps/msft.sme.server-manager!servers/tools/msft.sme.module-seed!main?connection=sme-full1.redmond.corp.microsoft.com"
        if (linkType === NotificationLinkType.Absolute) {
            return link;
        }

        // null linkType behaves like NotificationLinkType.RelativeToTool
        const pathname = linkType === NotificationLinkType.RelativeToRoot ?
            '/'
            : this.locationPathname || this.workItem && this.workItem.locationPathname;

        if (link && pathname) {
            this.showLinkInAlert = true;
            return MsftSme.trimEnd(pathname, '/') + '/' + MsftSme.trimStart(link, '/');
        }

        this.showLinkInAlert = false;
        return pathname;
    }

    private formatMessage(template: string): string {
        const parameters = this.findParameters(template);
        const message = this.replaceParameters(template, parameters);
        return message;
    }

    private findParameters(template: string): string[] {
        const results: string[] = [];
        if (!template) {
            return results;
        }

        const segments = template.split('{{');
        for (const seg of segments) {
            const index = seg.indexOf('}}');
            if (index > 0) {
                results.push(seg.substring(0, index));
            }
        }

        return results;
    }

    private replaceParameters(message: string, parameters: string[]): string {
        for (const param of parameters) {
            if (param === 'percent') {
                if (typeof this.progressPercent === 'number') {
                    message = message.replaceAll('{{percent}}', '' + this.progressPercent);
                }
            } else if (param === 'objectName') {
                if (this.workItem && this.workItem.objectName) {
                    message = message.replaceAll('{{objectName}}', this.workItem.objectName);
                }
            } else {
                if (this.object) {
                    const segments = param.split('.');
                    let target = this.object;
                    for (const seg of segments) {
                        if (target[seg]) {
                            target = target[seg];
                        } else {
                            target = null;
                            break;
                        }
                    }

                    if (target) {
                        message = message.replaceAll('{{' + param + '}}', '' + target);
                    }
                }
            }
        }

        return message;
    }
}
